// DistillModal: shows two parallel reviews of what the LLM extracted from
// the current thread — *cards* (crystallised content the user effectively
// said) and *gaps* (open items the user needs to resolve). The user picks
// which to commit; on accept, the parent hands the chosen lists to the
// agent which calls write_card for each card and propose_card for each gap.
//
// Stream model:
//   - parent kicks off `api.distillThread(threadId, onEvent)`
//   - while streaming, modal shows progress with a tick-driven bar
//   - on `done`, switch to the review list with everything pre-checked
//   - "Commit selected" → close modal + parent fires the agent with a
//     structured user message that splits cards vs. gaps for routing.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CardProposal, DistillMode, GapProposal } from '../api';

interface Props {
  /** When true the modal is open. */
  open: boolean;
  /** Count of proposals (cards + gaps) that have begun streaming so far —
   *  surfaced as concrete liveness ("Extracted N items so far") while the
   *  full result hasn't arrived yet. */
  itemsSoFar: number;
  /** Null while streaming; populated once the server emits `done`. */
  cards: CardProposal[] | null;
  /** Null while streaming; populated once the server emits `done`. */
  gaps: GapProposal[] | null;
  errorMsg: string | null;
  /** Which mode this distill run is using. Drives the segmented control's
   *  active state and the heading copy. */
  mode: DistillMode;
  /** Re-run with a different mode. Closes nothing — the same modal stays
   *  open and re-streams. Disabled while a run is in flight. */
  onChangeMode: (next: DistillMode) => void;
  onClose: () => void;
  /** User accepted a subset; parent forwards them to the agent. */
  onAccept: (chosen: { cards: CardProposal[]; gaps: GapProposal[] }) => void;
}

/** i18n key per distill mode — resolved at render time. */
const MODE_LABEL_KEY: Record<DistillMode, string> = {
  rescue: 'distill.modeRescue',
  decompose: 'distill.modeDecompose',
  audit: 'distill.modeAudit',
};

const MODE_HINT_KEY: Record<DistillMode, string> = {
  rescue: 'distill.hintRescue',
  decompose: 'distill.hintDecompose',
  audit: 'distill.hintAudit',
};

export function DistillModal({
  open,
  itemsSoFar,
  cards,
  gaps,
  errorMsg,
  mode,
  onChangeMode,
  onClose,
  onAccept,
}: Props) {
  const { t } = useTranslation();
  const [selectedCards, setSelectedCards] = useState<Set<number>>(new Set());
  const [selectedGaps, setSelectedGaps] = useState<Set<number>>(new Set());

  // Pre-check everything on arrival — the LLM already filtered for
  // crystallised vs. unresolved, so the assumption is the user wants to
  // commit it all unless they uncheck.
  useEffect(() => {
    if (cards) setSelectedCards(new Set(cards.map((_, i) => i)));
  }, [cards]);
  useEffect(() => {
    if (gaps) setSelectedGaps(new Set(gaps.map((_, i) => i)));
  }, [gaps]);

  if (!open) return null;

  const toggleCard = (i: number) =>
    setSelectedCards((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  const toggleGap = (i: number) =>
    setSelectedGaps((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const accept = () => {
    if (!cards || !gaps) return;
    onAccept({
      cards: cards.filter((_, i) => selectedCards.has(i)),
      gaps: gaps.filter((_, i) => selectedGaps.has(i)),
    });
  };

  const ready = cards !== null && gaps !== null;
  const totalProposed = (cards?.length ?? 0) + (gaps?.length ?? 0);
  const totalSelected = selectedCards.size + selectedGaps.size;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 200,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--paper-0, white)',
          borderRadius: 6,
          width: 720,
          maxWidth: '92vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 24px rgba(0,0,0,.18)',
        }}
      >
        <div
          style={{
            padding: '12px 16px 8px',
            borderBottom: '1px solid var(--rule)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <strong style={{ fontSize: 14 }}>{t('distill.heading')}</strong>
            <span style={{ flex: 1 }} />
            <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
              {t('distill.headerHint')}
            </span>
          </div>
          <ModeSwitcher
            mode={mode}
            onChangeMode={onChangeMode}
            disabled={!cards && !errorMsg}
          />
        </div>

        {!ready && !errorMsg && (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div
              className="mono"
              style={{ fontSize: 11, color: 'var(--ink-2)', marginBottom: 10 }}
            >
              {t('distill.scanning')}<span className="caret" />
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-1)',
                marginTop: 4,
                fontVariantNumeric: 'tabular-nums',
                animation: itemsSoFar === 0 ? 'pulse 1.4s ease-in-out infinite' : undefined,
              }}
            >
              {itemsSoFar === 0
                ? t('distill.reading')
                : t('distill.extractedSoFar', { count: itemsSoFar })}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                marginTop: 6,
                fontStyle: 'italic',
              }}
            >
              {t('distill.scanningHint')}
            </div>
          </div>
        )}

        {errorMsg && (
          <div style={{ padding: 24 }}>
            <div style={{ color: 'var(--red, #c44)', fontSize: 13, marginBottom: 12 }}>
              {t('distill.failed', { message: errorMsg })}
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '6px 14px',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                background: 'white',
                cursor: 'pointer',
              }}
            >
              {t('common.close')}
            </button>
          </div>
        )}

        {ready && totalProposed === 0 && (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 8 }}>
              {t('distill.nothingToExtract')}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginBottom: 16 }}>
              {t('distill.nothingToExtractHint')}
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '6px 14px',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                background: 'white',
                cursor: 'pointer',
              }}
            >
              {t('common.close')}
            </button>
          </div>
        )}

        {ready && totalProposed > 0 && (
          <>
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
              {cards && cards.length > 0 && (
                <Section
                  title={t('distill.cardsSection', { selected: selectedCards.size, total: cards.length })}
                  hint={t('distill.cardsSectionHint')}
                  tone="ink"
                >
                  {cards.map((p, i) => (
                    <CardRow
                      key={i}
                      checked={selectedCards.has(i)}
                      onToggle={() => toggleCard(i)}
                      kindLabel={p.kind.toUpperCase()}
                      title={p.title}
                      body={p.body}
                      rationale={p.rationale}
                    />
                  ))}
                </Section>
              )}
              {gaps && gaps.length > 0 && (
                <Section
                  title={t('distill.gapsSection', { selected: selectedGaps.size, total: gaps.length })}
                  hint={t('distill.gapsSectionHint')}
                  tone="amber"
                >
                  {gaps.map((g, i) => (
                    <CardRow
                      key={i}
                      checked={selectedGaps.has(i)}
                      onToggle={() => toggleGap(i)}
                      kindLabel={`✦ ${g.proposed_kind.toUpperCase()}`}
                      title={g.title}
                      body={g.body}
                      ambiguityReason={g.ambiguity_reason}
                      blocks={g.blocks}
                      priority={g.priority}
                      rationale={g.rationale}
                      tone="amber"
                    />
                  ))}
                </Section>
              )}
            </div>
            <div
              style={{
                padding: '10px 16px',
                borderTop: '1px solid var(--rule)',
                display: 'flex',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                {t('distill.selectedCount', { selected: totalSelected, total: totalProposed })}
                {cards && gaps && cards.length > 0 && gaps.length > 0 && (
                  <>
                    {' '}
                    {t('distill.selectedBreakdown', {
                      cards: selectedCards.size,
                      gaps: selectedGaps.size,
                    })}
                  </>
                )}
              </span>
              <span style={{ flex: 1 }} />
              <button
                onClick={onClose}
                style={{
                  fontSize: 12,
                  padding: '5px 12px',
                  border: '1px solid var(--rule)',
                  borderRadius: 3,
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={accept}
                disabled={totalSelected === 0}
                style={{
                  fontSize: 12,
                  padding: '5px 14px',
                  border: '1px solid var(--blue, #3b82f6)',
                  borderRadius: 3,
                  background:
                    totalSelected === 0 ? 'var(--ink-4, #ddd)' : 'var(--blue, #3b82f6)',
                  color: 'white',
                  cursor: totalSelected === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {t('distill.commit', { count: totalSelected })}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  hint,
  tone,
  children,
}: {
  title: string;
  hint: string;
  tone: 'ink' | 'amber';
  children: React.ReactNode;
}) {
  const color = tone === 'amber' ? 'var(--amber)' : 'var(--ink-2)';
  return (
    <div style={{ marginBottom: 4 }}>
      <div
        style={{
          padding: '8px 16px 4px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          borderBottom: '1px solid var(--rule-s, rgba(0,0,0,.06))',
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 10.5,
            color,
            fontWeight: 700,
            letterSpacing: '.1em',
          }}
        >
          {title}
        </span>
        <span style={{ fontSize: 10.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          {hint}
        </span>
      </div>
      {children}
    </div>
  );
}

function CardRow({
  checked,
  onToggle,
  kindLabel,
  title,
  body,
  ambiguityReason,
  blocks,
  priority,
  rationale,
  tone,
}: {
  checked: boolean;
  onToggle: () => void;
  kindLabel: string;
  title: string;
  body: string;
  ambiguityReason?: string;
  blocks?: string[];
  priority?: 'p0' | 'p1' | 'p2' | 'p3';
  rationale?: string;
  tone?: 'amber';
}) {
  const { t } = useTranslation();
  const accent = tone === 'amber' ? 'rgba(212,160,23,0.05)' : 'rgba(59,130,246,0.04)';
  const kindColor = tone === 'amber' ? 'var(--amber)' : 'var(--ink-3)';
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 16px',
        borderBottom: '1px solid var(--rule-s, rgba(0,0,0,.06))',
        cursor: 'pointer',
        background: checked ? accent : 'transparent',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{ marginTop: 4 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: kindColor,
              letterSpacing: '.06em',
              fontWeight: 700,
            }}
          >
            {kindLabel}
          </span>
          {priority && <PriorityChip priority={priority} />}
          <strong style={{ fontSize: 13 }}>{title}</strong>
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
            marginBottom: 4,
          }}
        >
          {body}
        </div>
        {ambiguityReason && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              fontStyle: 'italic',
              marginBottom: 4,
            }}
          >
            {t('distill.whyPrefix')} {ambiguityReason}
          </div>
        )}
        {blocks && blocks.length > 0 && (
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: 'var(--ink-3)',
              marginBottom: 4,
            }}
          >
            {t('distill.blocksPrefix')} {blocks.join(', ')}
          </div>
        )}
        {rationale && (
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--ink-3)',
              fontStyle: 'italic',
            }}
          >
            → {rationale}
          </div>
        )}
      </div>
    </label>
  );
}

/** Segmented control across the three distill modes. Re-runs on click;
 *  `disabled` while a stream is in flight so we don't fire two passes
 *  concurrently against the same thread. */
function ModeSwitcher({
  mode,
  onChangeMode,
  disabled,
}: {
  mode: DistillMode;
  onChangeMode: (next: DistillMode) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const options: DistillMode[] = ['rescue', 'decompose', 'audit'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        style={{
          display: 'inline-flex',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          overflow: 'hidden',
          alignSelf: 'flex-start',
        }}
      >
        {options.map((m) => {
          const active = m === mode;
          return (
            <button
              key={m}
              onClick={() => !active && !disabled && onChangeMode(m)}
              disabled={disabled || active}
              className="mono"
              style={{
                fontSize: 10.5,
                padding: '4px 12px',
                border: 'none',
                background: active ? 'var(--ink-1, #333)' : 'white',
                color: active ? 'white' : 'var(--ink-2)',
                cursor: active ? 'default' : disabled ? 'wait' : 'pointer',
                letterSpacing: '.08em',
              }}
            >
              {t(MODE_LABEL_KEY[m]).toUpperCase()}
            </button>
          );
        })}
      </div>
      <span style={{ fontSize: 10.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
        {t(MODE_HINT_KEY[mode])}
      </span>
    </div>
  );
}

/** Priority chip — p0 red, p1 amber, p2 green, p3 ink. Compact, mono. */
function PriorityChip({ priority }: { priority: 'p0' | 'p1' | 'p2' | 'p3' }) {
  const palette = {
    p0: { bg: 'rgba(220,53,69,.12)', fg: '#c1392b' },
    p1: { bg: 'rgba(212,160,23,.15)', fg: 'var(--amber, #b08a18)' },
    p2: { bg: 'rgba(46,160,80,.12)', fg: '#2c8a48' },
    p3: { bg: 'rgba(0,0,0,.06)', fg: 'var(--ink-3)' },
  }[priority];
  return (
    <span
      className="mono"
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '.06em',
        padding: '1px 5px',
        borderRadius: 2,
        background: palette.bg,
        color: palette.fg,
      }}
    >
      {priority.toUpperCase()}
    </span>
  );
}
